import { format, formatISO, isValid, parse, parseISO } from 'date-fns';
import { Injectable } from '@angular/core';
import { LogService } from '../../util/log.service';
import { IBookingModel } from '../booking/booking-model.interface';
import { IFlatModel } from '../flat/flat-model.interface';
import { II11Transformer } from '../generic-api-client.service';
import { IPaymentTransactionModel } from '../payment-transaction/payment-transaction-model.interface';
import { IUserModel } from '../user/user-model.interface';
import { IParkingRequestModel } from './parking-request-model.interface';

@Injectable({
  providedIn: 'root'
})
export class ParkingRequestTransformService implements II11Transformer<IParkingRequestModel> {
  constructor(private readonly log: LogService) {}

  transformItem(item: IParkingRequestModel): IParkingRequestModel {
    if (!item) {
      return {} as IParkingRequestModel;
    }
    const transformedItem: IParkingRequestModel = { ...item };
    const dateTimeFields: (keyof Pick<IParkingRequestModel, 'created_at' | 'updated_at'>)[] = ['created_at', 'updated_at'];
    const dateOnlyFields: (keyof Pick<IParkingRequestModel, 'start_date' | 'end_date'>)[] = ['start_date', 'end_date'];

    dateTimeFields.forEach((field) => {
      const originalDateString = item[field] as string | undefined;
      if (originalDateString && typeof originalDateString === 'string') {
        const date = parseISO(originalDateString);
        if (isValid(date)) {
          (transformedItem[field] as any) = format(date, 'yyyy-MM-dd HH:mm:ss');
        } else {
          this.log.warn(`ParkingRequestTransformService: Invalid ${String(field)} for parking request ID ${item.id}: ${originalDateString}`);
        }
      }
    });

    dateOnlyFields.forEach((field) => {
      const originalDateString = item[field] as string | undefined;
      if (originalDateString && typeof originalDateString === 'string') {
        const date = parseISO(originalDateString);
        if (isValid(date)) {
          (transformedItem[field] as any) = format(date, 'yyyy-MM-dd');
        } else {
          this.log.warn(`ParkingRequestTransformService: Invalid ${String(field)} for parking request ID ${item.id}: ${originalDateString}`);
        }
      }
    });
    return transformedItem;
  }

  serializeItem(item: IParkingRequestModel): Partial<IParkingRequestModel> {
    if (!item) {
      return {};
    }

    const { id, ['@id']: iri, created_at, updated_at, createdBy, updatedBy, ...restOfItem } = item;
    const serializedItem: Partial<IParkingRequestModel> = { ...restOfItem };

    // Serialize date fields back to ISO strings
    const dateFieldsToSerialize: (keyof Pick<IParkingRequestModel, 'start_date' | 'end_date'>)[] = ['start_date', 'end_date'];
    dateFieldsToSerialize.forEach((field) => {
      const dateString = item[field] as string | undefined; // Use item[field] as it might be original ISO or transformed 'yyyy-MM-dd'
      if (dateString && typeof dateString === 'string') {
        // Try parsing as 'yyyy-MM-dd' first (if transformed), then as ISO if that fails or it's the original value
        let date = parse(dateString, 'yyyy-MM-dd', new Date());
        if (!isValid(date)) {
          date = parseISO(dateString);
        }
        if (isValid(date)) {
          (serializedItem[field] as any) = formatISO(date); // Format to full ISO string
        } else {
          this.log.warn(
            `ParkingRequestTransformService: Could not serialize invalid ${String(field)} for parking request ID ${item.id}: ${dateString}`
          );
          (serializedItem[field] as any) = dateString; // Keep original if invalid and unparsable
        }
      }
    });

    // Serialize nested objects and arrays to IRIs
    if (item.paymentTransactions && Array.isArray(item.paymentTransactions)) {
      serializedItem.paymentTransactions = item.paymentTransactions
        .map((pt) => {
          if (typeof pt === 'object' && (pt as IPaymentTransactionModel)['@id']) {
            return (pt as IPaymentTransactionModel)['@id'] as any;
          }
          return pt; // Should be IRI string already or handle error
        })
        .filter((ptIri) => ptIri != null) as any[];
    }

    if (item.booking && typeof item.booking === 'object' && (item.booking as IBookingModel)['@id']) {
      serializedItem.booking = (item.booking as IBookingModel)['@id'] as any;
    } else if (typeof item.booking === 'string') {
      serializedItem.booking = item.booking as any; // Assume it's an IRI
    }

    if (item.parkingPlace && typeof item.parkingPlace === 'object' && (item.parkingPlace as IFlatModel)['@id']) {
      serializedItem.parkingPlace = (item.parkingPlace as IFlatModel)['@id'] as any;
    } else if (typeof item.parkingPlace === 'string') {
      serializedItem.parkingPlace = item.parkingPlace as any; // Assume it's an IRI
    }

    // 'invoice' is already a string, assumed to be an IRI. No transformation needed.
    // Read-only fields (id, @id, created_at, updated_at, createdBy, updatedBy) are already excluded or will be by API.

    return serializedItem;
  }
}
